import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../client'

// Hits the Dispatch #1-redacted GET /api/profile/:userId endpoint.
// Self / guardian → full payload (MINIMAL + SELF_ONLY); non-self → minimal only.
// Consumers must conditionally render fields that may be absent.
export interface ProfileResponse {
  id: number
  firstName: string
  lastName: string
  profilePhoto: string | null
  motherName?: string | null
  fatherName?: string | null
  location?: string | null
  isDeceased?: boolean
  connectedRelativesCount?: number
  // SELF_ONLY (only present for self / guardian)
  email?: string
  phoneNumber?: string | null
  dateOfBirth?: string | null
  gender?: 'male' | 'female' | 'other' | null
  bio?: string | null
  languageCommunity?: string | null
  languageCommunityId?: number | null
  preferredLanguage?: string
  kinshipLabelPreference?: 'telugu_script' | 'romanized' | 'both'
}

export function useProfile(userId: number | string | undefined) {
  return useQuery({
    queryKey: ['profile', String(userId ?? '')],
    queryFn: async () => {
      const res = await apiClient.get<ProfileResponse>(`/api/profile/${userId}`)
      return res.data
    },
    enabled: !!userId,
  })
}

export function useUpdateProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ userId, form }: { userId: number; form: Record<string, unknown> }) => {
      const res = await apiClient.put(`/api/profile/${userId}`, form)
      return res.data
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['profile', String(vars.userId)] })
    },
  })
}

export function useUploadProfilePhoto() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ userId, file }: { userId: number; file: File }) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('userId', String(userId))
      const res = await apiClient.post('/api/profile/upload/profile-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['profile', String(vars.userId)] })
    },
  })
}
